import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faLock,
    faHandHoldingUsd,
    faMagnifyingGlassDollar,
    faCreditCard,
    faGift,
    faShareNodes,
    faCompressAlt,
    faFileCsv,
    faLightbulb,
    faShuffle
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

// The app has grown a lot of features people never stumble upon, so surface
// one of them at a time on the home page.
const TIPS: { key: string; icon: IconDefinition }[] = [
    { key: 'lockEvent', icon: faLock },
    { key: 'paymentMethod', icon: faHandHoldingUsd },
    { key: 'whyThisAmount', icon: faMagnifyingGlassDollar },
    { key: 'bankInfo', icon: faCreditCard },
    { key: 'sponsor', icon: faGift },
    { key: 'shareLink', icon: faShareNodes },
    { key: 'compactMode', icon: faCompressAlt },
    { key: 'export', icon: faFileCsv },
];

const TipsCard: React.FC = () => {
    const { t } = useTranslation();
    // Start somewhere random so returning visitors don't always see the same tip
    const [index, setIndex] = useState(() => Math.floor(Math.random() * TIPS.length));

    const tip = TIPS[index];

    return (
        <section className="pb-12">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="glass-card glass-shimmer-hover p-5 sm:p-6">
                    <div className="flex items-start gap-4">
                        <div className="glass-sub-panel inline-flex items-center justify-center w-12 h-12 rounded-xl flex-shrink-0">
                            <FontAwesomeIcon icon={tip.icon} className="text-xl text-indigo-600 dark:text-indigo-400" aria-hidden="true"/>
                        </div>

                        <div className="flex-1 min-w-0" aria-live="polite">
                            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 mb-1">
                                <FontAwesomeIcon icon={faLightbulb} aria-hidden="true"/>
                                {t('home.tips.label')}
                            </p>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1 text-balance">
                                {t(`home.tips.items.${tip.key}.title`)}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300">
                                {t(`home.tips.items.${tip.key}.text`)}
                            </p>
                        </div>
                    </div>

                    {/* Each dot is a 24px target around an 8px mark: the mark
                        alone was too small to hit with a finger. That no longer
                        fits beside the button on a phone, so there the row wraps
                        into two, centred. */}
                    <div className="flex flex-wrap items-center justify-center sm:justify-between gap-x-4 gap-y-2 mt-4 pt-3 border-t border-gray-200/60 dark:border-gray-700/60">
                        <div className="flex items-center">
                            {TIPS.map((item, i) => (
                                <button
                                    key={item.key}
                                    type="button"
                                    onClick={() => setIndex(i)}
                                    aria-label={t('home.tips.goTo', { index: i + 1 })}
                                    aria-current={i === index}
                                    className="group h-6 min-w-6 px-1 flex items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                                >
                                    <span
                                        aria-hidden="true"
                                        className={`h-2 rounded-full transition-all duration-200 ${
                                            i === index
                                                ? 'w-5 bg-indigo-500'
                                                : 'w-2 bg-gray-300 dark:bg-gray-600 group-hover:bg-gray-400 dark:group-hover:bg-gray-500'
                                        }`}
                                    />
                                </button>
                            ))}
                        </div>

                        <button
                            type="button"
                            onClick={() => setIndex((i) => (i + 1) % TIPS.length)}
                            className="glass-btn-secondary flex items-center gap-2 !px-4 !py-2 text-sm flex-shrink-0"
                        >
                            <FontAwesomeIcon icon={faShuffle} aria-hidden="true"/>
                            {t('home.tips.next')}
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default TipsCard;
